import { createFetch } from '@vueuse/core';
import KeycloakService from '@/security/KeycloakService';
import { publicServerApiBaseUrl, serverApiBaseUrl } from './common';
import imageCompression from 'browser-image-compression';
import type {
  ImageState,
  ImgTextState,
} from 'src/components/image-text-ensemble/types';
import type { ImageTransform } from 'src/types/imageTransform';

const baseUrl = serverApiBaseUrl();
const publicBaseUrl = publicServerApiBaseUrl();
const teamDataUrl =
  typeof import.meta.env.VITE_TEAM_DATA_URL === 'string'
    ? import.meta.env.VITE_TEAM_DATA_URL.trim()
    : '';
if (!teamDataUrl) {
  throw new Error(
    '[UserService] Missing required environment variable: VITE_TEAM_DATA_URL',
  );
}
const devApiToken = import.meta.env.VITE_DEV_API_TOKEN?.trim();
const imageBaseUrl =
  typeof import.meta.env.VITE_IMAGE_BASE_URL === 'string'
    ? import.meta.env.VITE_IMAGE_BASE_URL.trim()
    : 'https://totaltrivial.de/images';

const resolveAuthToken = async (): Promise<string | null> => {
  if (import.meta.env.DEV && devApiToken) {
    return devApiToken;
  }

  return KeycloakService.ensureToken();
};

const buildOptionalAuthHeaders = async (base?: HeadersInit) => {
  const headers = new Headers(base);
  const token = await resolveAuthToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
};

const resolvePoolImageUrl = (
  imageId?: string | null,
  location?: string | null,
  galleryVariant = 'bilder',
): string => {
  if (!imageId) {
    return '';
  }
  const trimmed = imageId.trim();
  if (!trimmed) {
    return '';
  }
  const base = imageBaseUrl.replace(/\/$/, '');
  const rawLocation = location?.trim() || 'pool';

  // Normalise gallery locations — strip any variant suffix
  // e.g. "gallery/bilder" → "gallery"
  const isGallery =
    rawLocation === 'gallery' || rawLocation.startsWith('gallery/');
  const safeLocation = isGallery ? 'gallery' : rawLocation;

  if (isGallery) {
    // Gallery imageId is "albumId/filename" — insert variant between them
    const slashIdx = trimmed.indexOf('/');
    if (slashIdx > 0) {
      const albumId = trimmed.substring(0, slashIdx);
      const filename = trimmed.substring(slashIdx + 1);
      const fileSuffix = /\.\w+$/.test(filename) ? '' : '.webp';
      return `${base}/${safeLocation}/${albumId}/${galleryVariant}/${filename}${fileSuffix}`;
    }
  }

  const suffix = /\.\w+$/.test(trimmed) ? '' : '.webp';
  return `${base}/${safeLocation}/${trimmed}${suffix}`;
};

const normalizePublicImageSrc = (
  candidate?: string | null,
  location?: string | null,
): string => {
  if (!candidate) {
    return '';
  }

  const trimmed = candidate.trim();
  if (!trimmed) {
    return '';
  }

  if (
    /^https?:\/\//i.test(trimmed) ||
    trimmed.startsWith('/') ||
    trimmed.startsWith('data:') ||
    trimmed.startsWith('blob:')
  ) {
    return trimmed;
  }

  return resolvePoolImageUrl(trimmed, location);
};

const useAuthedFetch = createFetch({
  baseUrl,
  options: {
    async beforeFetch({ options }) {
      const devToken = import.meta.env.VITE_DEV_API_TOKEN?.trim();
      if (import.meta.env.DEV && devToken) {
        const headers = new Headers(options.headers as HeadersInit | undefined);
        const method = (options.method ?? 'GET').toUpperCase();
        headers.set('Authorization', `Bearer ${devToken}`);
        const bodyIsFormData = options.body instanceof FormData;
        if (
          method !== 'GET' &&
          !headers.has('Content-Type') &&
          !bodyIsFormData
        ) {
          headers.set('Content-Type', 'application/json');
        }
        if (
          method !== 'GET' &&
          options.body &&
          typeof options.body === 'object' &&
          !(options.body instanceof FormData)
        ) {
          options.body = JSON.stringify(options.body);
        }
        options.headers = headers;
        return { options };
      }

      const headers = new Headers(options.headers as HeadersInit | undefined);
      const token = await KeycloakService.ensureToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      const method = (options.method ?? 'GET').toUpperCase();
      const bodyIsFormData = options.body instanceof FormData;
      if (method !== 'GET' && !headers.has('Content-Type') && !bodyIsFormData) {
        headers.set('Content-Type', 'application/json');
      }
      if (
        options.body &&
        typeof options.body === 'object' &&
        !(options.body instanceof FormData)
      ) {
        options.body = JSON.stringify(options.body);
      }

      options.headers = headers;
      return { options };
    },
  },
  fetchOptions: {
    mode: 'cors',
  },
});

export interface TeamImagePayload {
  baseUrl?: string;
  src?: string;
  transform?: ImageTransform;
}

export interface TeamMemberPayload {
  name: string;
  description: string;
  image: TeamImagePayload;
  imageState?: ImageState;
  imgText?: ImgTextState;
  sectionKey?: string;
  sectionId?: number;
}

type TeamApiMember = {
  id?: string;
  name?: string;
  description?: string;
  image_filename?: string;
  image_trim?: ImageTransform['trim'];
  image_width?: number;
  image_height?: number;
  imageUrl?: string;
  image_url?: string;
  image?: TeamImagePayload;
  imageState?: ImageState;
  imgText?: ImgTextState;
  sectionKey?: string;
  sectionId?: number;
};

type TeamLoadResponse = TeamMemberPayload[] | { teamMembers: TeamApiMember[] };

export interface TeamSaveRequest {
  filename: string;
  content: string;
}

export interface SectionObject {
  id: string;
  title?: string;
  subtitle?: string;
  img?: string | null;
  html: string;
  ctaLabel?: string;
  ctaHref?: string;
  sortOrder?: number;
  sectionKeyRaw?: string;
  recordId?: number;
  imageState?: ImageState;
  layout?: {
    imageSplitPercent?: number;
  };
  imgText?: ImgTextState;
}

export type SectionLoadResponse = SectionObject[];

export interface SectionSaveRequest {
  items: Array<{
    id?: number | null;
    section_key: string;
    title?: string;
    html: string;
    image?: {
      id?: string;
      location?: string;
      state?: {
        zoomPercent: number;
        offsetX: number;
        offsetY: number;
      };
    };
    image_split_percent?: number;
    sort_order?: number;
  }>;
  delete_ids?: number[];
}

export interface ImagePoolItem {
  id: string;
  imageType?: string;
  fileLocation?: string;
  url: string;
  thumbUrl?: string;
  filename?: string;
  source?: string;
  createdAt?: string;
  tags?: string[];
}

export type ImageListResponse = {
  images?: ImagePoolItem[];
  items?: ImagePoolItem[];
};

export type ImageUploadResultItem = {
  success: boolean;
  id?: string;
  pool?: string;
  filename?: string;
  source?: string;
  path?: string;
  record?: ImagePoolItem;
  error?: string;
  status?: number;
  duplicateId?: string;
  duplicateUrl?: string;
  similarity?: number | string;
  hammingDistance?: number;
};

export type ImageUploadResponse = {
  success: boolean;
  results: ImageUploadResultItem[];
};

const CLIENT_UPLOAD_MAX_DIMENSION = 1024;
const CLIENT_UPLOAD_WEBP_QUALITY = 0.8;
const WEBP_MIME_TYPE = 'image/webp';

const shouldClientProcessImage = (file: File) => {
  if (
    typeof window === 'undefined' ||
    typeof document === 'undefined' ||
    typeof URL === 'undefined'
  ) {
    return false;
  }
  if (!file.type.startsWith('image/')) {
    return false;
  }
  // Keep formats that would lose important properties when rasterized/re-encoded.
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') {
    return false;
  }
  return true;
};

const prepareImageForUpload = async (
  imageFile: File,
  filenameOverride?: string,
) => {
  const originalFilename = filenameOverride?.trim() || imageFile.name;
  if (!shouldClientProcessImage(imageFile)) {
    return { file: imageFile, filename: originalFilename };
  }

  try {
    const compressed = await imageCompression(imageFile, {
      maxWidthOrHeight: CLIENT_UPLOAD_MAX_DIMENSION,
      initialQuality: CLIENT_UPLOAD_WEBP_QUALITY,
      fileType: WEBP_MIME_TYPE,
      useWebWorker: true,
    });
    const file = new File([compressed], originalFilename, {
      type: WEBP_MIME_TYPE,
      lastModified: Date.now(),
    });
    return { file, filename: originalFilename };
  } catch (error) {
    console.warn('[uploadImage] client-side image preprocessing failed', error);
    return { file: imageFile, filename: originalFilename };
  }
};

export const loadSections = async (fetcher: typeof fetch = fetch) => {
  const headers = await buildOptionalAuthHeaders({
    'Content-Type': 'application/json',
    Accept: 'application/json',
  });
  const response = await fetcher(`${publicBaseUrl}/public/sections`, {
    // const response = await fetcher(`/public/data/sectionData.json`, {
    headers,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const payload = (await response.json()) as
    | SectionLoadResponse
    | { sections?: Array<Record<string, unknown>> };

  const sortBySortOrderWithinSectionKey = (items: SectionObject[]) => {
    const grouped = new Map<
      string,
      Array<{ item: SectionObject; index: number }>
    >();

    items.forEach((item, index) => {
      const key = String(item.sectionKeyRaw ?? item.id ?? 'actual');
      const existing = grouped.get(key) ?? [];
      existing.push({ item, index });
      grouped.set(key, existing);
    });

    const sortedGroups = new Map<string, SectionObject[]>();
    grouped.forEach((entries, key) => {
      const sorted = entries
        .slice()
        .sort((a, b) => {
          const aOrder = a.item.sortOrder;
          const bOrder = b.item.sortOrder;
          const aHas = Number.isFinite(aOrder);
          const bHas = Number.isFinite(bOrder);

          if (aHas && bHas && aOrder !== bOrder) {
            return (aOrder as number) - (bOrder as number);
          }
          if (aHas && !bHas) {
            return -1;
          }
          if (!aHas && bHas) {
            return 1;
          }
          return a.index - b.index;
        })
        .map(({ item }) => item);

      sortedGroups.set(key, sorted);
    });

    return items.map((item) => {
      const key = String(item.sectionKeyRaw ?? item.id ?? 'actual');
      const queue = sortedGroups.get(key);
      const next = queue?.shift();
      return next ?? item;
    });
  };

  const sectionsArray: Array<Record<string, unknown>> | null = Array.isArray(
    payload,
  )
    ? (payload as unknown as Array<Record<string, unknown>>)
    : payload?.sections && Array.isArray(payload.sections)
      ? (payload.sections as Array<Record<string, unknown>>)
      : null;

  if (sectionsArray) {
    const mapped = sectionsArray.map((section) => {
      const sectionRecord = section as Record<string, unknown>;
      const sectionKeyRaw =
        typeof sectionRecord.section_key === 'string'
          ? sectionRecord.section_key
          : typeof sectionRecord.section_key === 'number'
            ? String(sectionRecord.section_key)
            : typeof sectionRecord.id === 'string'
              ? sectionRecord.id
              : 'actual';
      const recordIdFromIdString =
        typeof sectionRecord.id === 'string' && /^\d+$/.test(sectionRecord.id)
          ? Number(sectionRecord.id)
          : undefined;
      const recordId =
        typeof sectionRecord.id === 'number'
          ? sectionRecord.id
          : typeof sectionRecord.section_id === 'number'
            ? sectionRecord.section_id
            : typeof sectionRecord.sectionId === 'number'
              ? sectionRecord.sectionId
              : recordIdFromIdString;
      const sectionKey = sectionKeyRaw.trim() || 'actual';
      const title =
        typeof sectionRecord.title === 'string' ? sectionRecord.title : '';
      const html =
        typeof sectionRecord.html === 'string' ? sectionRecord.html : '';

      const image =
        sectionRecord.image && typeof sectionRecord.image === 'object'
          ? (sectionRecord.image as Record<string, unknown>)
          : undefined;
      const imageId =
        image && typeof image.id === 'string' ? image.id : undefined;
      const imageLocation =
        image && typeof image.location === 'string'
          ? image.location
          : undefined;
      const imageUrl = imageId
        ? resolvePoolImageUrl(imageId, imageLocation)
        : undefined;
      const imageState =
        image && typeof image.state === 'object'
          ? (image.state as Record<string, unknown>)
          : undefined;

      const zoomPercent = Number(imageState?.zoomPercent ?? 0);
      const offsetX = Number(imageState?.offsetX ?? 0);
      const offsetY = Number(imageState?.offsetY ?? 0);

      const splitPercent = Number(sectionRecord.image_split_percent ?? NaN);
      const layout = Number.isFinite(splitPercent)
        ? { imageSplitPercent: Math.min(Math.max(splitPercent, 0), 100) }
        : undefined;

      const sortOrder = Number(sectionRecord.sort_order ?? NaN);
      const mapped: SectionObject = {
        id: sectionKey,
        title,
        html,
        img: imageId ?? null,
        sortOrder: Number.isFinite(sortOrder) ? sortOrder : undefined,
        sectionKeyRaw: sectionKeyRaw,
        recordId,
        imageState: imageId
          ? {
              zoomPercent: Number.isFinite(zoomPercent) ? zoomPercent : 0,
              offsetX: Number.isFinite(offsetX) ? offsetX : 0,
              offsetY: Number.isFinite(offsetY) ? offsetY : 0,
              src: imageUrl ?? imageId,
              imageId,
              location: imageLocation,
            }
          : undefined,
        layout,
      };

      return mapped;
    });

    return sortBySortOrderWithinSectionKey(mapped);
  }

  return [];
};

const extractImageId = (value?: string | null) => {
  if (!value) {
    return undefined;
  }
  const withoutQuery = value.split('?')[0]?.split('#')[0] ?? '';
  const filename = withoutQuery.split('/').filter(Boolean).pop() ?? '';
  if (!filename) {
    return undefined;
  }
  return filename.replace(/\.[^.]+$/, '');
};

export const buildSectionSaveRequest = (
  payload: SectionObject[],
  deleteIds?: number[],
): SectionSaveRequest => {
  const keyCounters: Record<string, number> = {};

  const resolveSectionKey = (rawKey: string) => {
    const normalized = rawKey.trim() || 'actual';
    const nextIndex = (keyCounters[normalized] ?? 0) + 1;
    keyCounters[normalized] = nextIndex;
    return { key: normalized, sortOrder: nextIndex };
  };

  const result: SectionSaveRequest = {
    items: payload.map((section) => {
      const rawKey = String(section.sectionKeyRaw ?? section.id);
      const { key: sectionKey, sortOrder: autoOrder } =
        resolveSectionKey(rawKey);
      const sortOrder = section.sortOrder ?? autoOrder;
      const resolvedContent = section.imgText?.content ?? section.html;
      const resolvedImageState = section.imgText?.image
        ? { ...section.imgText.image, ...section.imageState }
        : section.imageState;
      const resolvedLayout = section.imgText?.layout ?? section.layout;
      const imageId =
        resolvedImageState?.imageId ??
        section.imageState?.imageId ??
        extractImageId(
          resolvedImageState?.src ?? section.imageState?.src ?? section.img,
        );

      return {
        id: typeof section.recordId === 'number' ? section.recordId : null,
        section_key: sectionKey,
        title: section.title,
        html: resolvedContent,
        sort_order: sortOrder,
        image: imageId
          ? {
              id: imageId,
              location: resolvedImageState?.location ?? 'pool',
              state: {
                zoomPercent: resolvedImageState?.zoomPercent ?? 0,
                offsetX: resolvedImageState?.offsetX ?? 0,
                offsetY: resolvedImageState?.offsetY ?? 0,
              },
            }
          : undefined,
        image_split_percent: resolvedLayout?.imageSplitPercent,
      };
    }),
  };

  if (deleteIds?.length) {
    result.delete_ids = deleteIds;
  }

  return result;
};

export interface SectionSaveResponseItem {
  id: number;
  section_key: string;
  title?: string;
  sort_order?: number;
}

export interface SectionOrderUpdateItem {
  id: number;
  sortOrder: number;
}

export interface SectionOrderUpdateResponse {
  success?: boolean;
  sectionKey?: string;
  updated?: number;
  assigned?: number;
  total?: number;
}

export const saveSections = async (
  payload: SectionObject[],
  deleteIds?: number[],
): Promise<SectionSaveResponseItem[]> => {
  const request = buildSectionSaveRequest(payload, deleteIds);

  const token = await resolveAuthToken();
  if (!token) {
    throw new Error('User is not authenticated');
  }

  const response = await fetch(`${publicBaseUrl}/content/sections`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
    mode: 'cors',
  });

  if (!response.ok) {
    let message: string | undefined;
    let details: string | undefined;
    try {
      const payload = (await response.json()) as {
        message?: string;
        error?: string;
        details?: unknown;
      } | null;
      message = payload?.message || payload?.error;
      if (payload?.details) {
        details = JSON.stringify(payload.details);
      }
    } catch {
      try {
        const text = await response.text();
        details = text?.trim() || undefined;
      } catch {
        details = undefined;
      }
    }
    const statusInfo = `HTTP ${response.status}`;
    const detailInfo = details ? ` · ${details}` : '';
    throw new Error(
      message
        ? `${message} (${statusInfo}${detailInfo})`
        : `Upload fehlgeschlagen (${statusInfo}${detailInfo})`,
    );
  }

  try {
    const body = (await response.json()) as {
      items?: SectionSaveResponseItem[];
    } | null;
    return body?.items ?? [];
  } catch {
    return [];
  }
};

export const updateSectionOrder = async (
  sectionKey: string,
  items: SectionOrderUpdateItem[],
): Promise<SectionOrderUpdateResponse> => {
  const resolvedSectionKey = sectionKey.trim();
  if (!resolvedSectionKey) {
    throw new Error('sectionKey is required.');
  }
  if (!items.length) {
    return { success: true, sectionKey: resolvedSectionKey, updated: 0 };
  }

  const token = await resolveAuthToken();
  if (!token) {
    throw new Error('User is not authenticated');
  }

  const response = await fetch(
    `${publicBaseUrl}/content/sections/update_order`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        section_key: resolvedSectionKey,
        items: items.map((item) => ({
          id: item.id,
          sort_order: item.sortOrder,
        })),
      }),
      mode: 'cors',
    },
  );

  if (!response.ok) {
    let message: string | undefined;
    let details: string | undefined;
    try {
      const payload = (await response.json()) as {
        message?: string;
        error?: string;
        details?: unknown;
      } | null;
      message = payload?.message || payload?.error;
      if (payload?.details) {
        details = JSON.stringify(payload.details);
      }
    } catch {
      try {
        const text = await response.text();
        details = text?.trim() || undefined;
      } catch {
        details = undefined;
      }
    }

    const statusInfo = `HTTP ${response.status}`;
    const detailInfo = details ? ` · ${details}` : '';
    throw new Error(
      message
        ? `${message} (${statusInfo}${detailInfo})`
        : `Sortierung konnte nicht aktualisiert werden (${statusInfo}${detailInfo})`,
    );
  }

  try {
    return (await response.json()) as SectionOrderUpdateResponse;
  } catch {
    return { success: true, sectionKey: resolvedSectionKey };
  }
};

const toTeamMemberPayload = (member: TeamApiMember): TeamMemberPayload => {
  if (member.image) {
    return {
      name: member.name ?? '',
      description: member.description ?? '',
      image: member.image,
      imageState: member.imageState,
      imgText: member.imgText,
      sectionKey: member.sectionKey,
    };
  }

  const imageUrl = member.imageUrl ?? member.image_url ?? '';
  const transform =
    member.image_trim || member.image_width || member.image_height
      ? {
          trim: member.image_trim,
          width: member.image_width,
          height: member.image_height,
        }
      : undefined;

  return {
    name: member.name ?? '',
    description: member.description ?? '',
    image: {
      baseUrl: imageUrl,
      src: imageUrl,
      transform,
    },
    imageState: member.imageState,
    imgText: member.imgText,
    sectionKey: member.sectionKey,
  };
};

export const loadTeamMembers = async (fetcher: typeof fetch = fetch) => {
  try {
    const sections = await loadSections(fetcher);
    const teamSections = sections
      .filter((section) => section.id === 'team')
      .slice()
      .sort((a, b) => {
        const aOrder = a.sortOrder ?? Number.POSITIVE_INFINITY;
        const bOrder = b.sortOrder ?? Number.POSITIVE_INFINITY;
        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }
        return (a.title ?? a.id).localeCompare(b.title ?? b.id, 'de');
      });

    if (teamSections.length) {
      return teamSections.map<TeamMemberPayload>((section) => {
        const title = section.title ?? '';
        const html = section.html ?? '';
        const headingMatch = html.match(/<h2[^>]*>(.*?)<\/h2>/i);
        const name = title || (headingMatch?.[1] ?? '').trim();
        const description = headingMatch
          ? html.replace(headingMatch[0], '').trim()
          : html;

        const splitPercent = Number(section.layout?.imageSplitPercent ?? NaN);
        const normalizedLayout = Number.isFinite(splitPercent)
          ? { imageSplitPercent: splitPercent }
          : undefined;

        const imageId =
          section.imageState?.imageId ??
          (typeof section.img === 'string' ? section.img : undefined);
        const imageLocation =
          section.imageState && typeof section.imageState.location === 'string'
            ? section.imageState.location
            : undefined;
        const imageSrc = normalizePublicImageSrc(
          section.imageState?.src ?? imageId,
          imageLocation,
        );

        return {
          name,
          description,
          image: {
            src: imageSrc,
          },
          imageState: section.imageState
            ? {
                zoomPercent: section.imageState.zoomPercent ?? 0,
                offsetX: section.imageState.offsetX ?? 0,
                offsetY: section.imageState.offsetY ?? 0,
                src: imageSrc,
                imageId: section.imageState.imageId ?? imageId,
                location: section.imageState.location,
              }
            : imageSrc
              ? {
                  zoomPercent: 0,
                  offsetX: 0,
                  offsetY: 0,
                  src: imageSrc,
                  imageId,
                }
              : undefined,
          imgText: section.imgText ?? {
            content: html,
            image: {
              zoomPercent: section.imageState?.zoomPercent ?? 0,
              offsetX: section.imageState?.offsetX ?? 0,
              offsetY: section.imageState?.offsetY ?? 0,
              src: imageSrc,
              imageId: section.imageState?.imageId ?? imageId,
              location: imageLocation,
            },
            layout: normalizedLayout,
          },
          sectionKey: section.sectionKeyRaw ?? section.id,
          sectionId: section.recordId,
        };
      });
    }
  } catch (error) {
    console.warn('[UserService] Failed to load team via sections', error);
  }

  const requestUrl = teamDataUrl;
  const headers = await buildOptionalAuthHeaders({
    'Content-Type': 'application/json',
    Accept: 'application/json',
  });
  const response = await fetcher(requestUrl, {
    headers,
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const payload = (await response.json()) as TeamLoadResponse;
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload?.teamMembers && Array.isArray(payload.teamMembers)) {
    return payload.teamMembers.map((member) => toTeamMemberPayload(member));
  }
  return [];
};

export interface TeamMemberSaveEntry {
  member: TeamMemberPayload;
  /** 1-based position in the full list (used as sort_order) */
  sortOrder: number;
}

export const saveTeamMembers = async (
  entries: TeamMemberSaveEntry[],
  deleteIds?: number[],
): Promise<SectionSaveResponseItem[]> => {
  const sections: SectionObject[] = entries.map(({ member, sortOrder }) => {
    const resolvedKey = 'team';
    const resolvedImgText = member.imgText ?? {
      content: `<h2>${member.name}</h2>${member.description ?? ''}`,
      image: {
        zoomPercent: member.imageState?.zoomPercent ?? 0,
        offsetX: member.imageState?.offsetX ?? 0,
        offsetY: member.imageState?.offsetY ?? 0,
        src: member.imageState?.src ?? member.image.baseUrl ?? member.image.src,
        transform: member.image.transform,
        imageId: member.imageState?.imageId,
        location: member.imageState?.location,
      },
    };

    return {
      id: resolvedKey,
      title: member.name,
      html: resolvedImgText.content,
      img: resolvedImgText.image?.src ?? null,
      sectionKeyRaw: member.sectionKey,
      recordId: member.sectionId,
      sortOrder,
      imageState: {
        zoomPercent: resolvedImgText.image?.zoomPercent ?? 0,
        offsetX: resolvedImgText.image?.offsetX ?? 0,
        offsetY: resolvedImgText.image?.offsetY ?? 0,
        src: resolvedImgText.image?.src,
        imageId: resolvedImgText.image?.imageId ?? member.imageState?.imageId,
        location:
          resolvedImgText.image?.location ?? member.imageState?.location,
      },
      layout: resolvedImgText.layout,
      imgText: resolvedImgText,
    };
  });

  return saveSections(sections, deleteIds);
};

export const uploadImage = async (
  imageFile: File,
  filenameOverride?: string,
  tags?: string[],
): Promise<ImageUploadResponse> => {
  if (!(imageFile instanceof File)) {
    throw new Error('Invalid image file.');
  }

  const { file: preparedFile, filename: preparedFilename } =
    await prepareImageForUpload(imageFile, filenameOverride);

  const formData = new FormData();
  formData.append('file', preparedFile, preparedFilename);
  if (Array.isArray(tags)) {
    formData.append('tags', JSON.stringify(tags));
  }

  const token = await resolveAuthToken();
  if (!token) {
    throw new Error('User is not authenticated');
  }

  const response = await fetch(`${publicBaseUrl}/image-pool`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: formData,
    mode: 'cors',
  });

  if (!response.ok) {
    if (response.status === 409) {
      const payload = (await response.json()) as Partial<ImageUploadResponse> | null;
      return {
        success: payload?.success ?? false,
        results: Array.isArray(payload?.results) ? payload.results : [],
      };
    }
    const message = await readErrorMessage(response);
    throw new Error(message || 'Upload fehlgeschlagen');
  }

  const payload = (await response.json()) as ImageUploadResponse;
  return {
    success: payload.success ?? true,
    results: Array.isArray(payload.results) ? payload.results : [],
  };
};

const readErrorMessage = async (response: Response) => {
  if (!response || response.ok) {
    return undefined;
  }

  try {
    const payload = (await response.json()) as { message?: string } | null;
    return payload?.message;
  } catch {
    return undefined;
  }
};

export const updateImageInPool = async (
  imageId: string,
  payload: { tags?: string[]; file?: File },
): Promise<ImagePoolItem | null> => {
  if (!imageId) {
    throw new Error('Invalid image id.');
  }

  const token = await resolveAuthToken();
  if (!token) {
    throw new Error('User is not authenticated');
  }

  const formData = new FormData();
  if (payload.file instanceof File) {
    const { file: preparedFile, filename: preparedFilename } =
      await prepareImageForUpload(payload.file);
    formData.append('file', preparedFile, preparedFilename);
  }
  if (Array.isArray(payload.tags)) {
    formData.append('tags', JSON.stringify(payload.tags));
  }

  const response = await fetch(
    `${publicBaseUrl}/image-pool/pool/${encodeURIComponent(imageId)}`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: formData,
      mode: 'cors',
    },
  );

  if (!response.ok) {
    const message = await readErrorMessage(response);
    throw new Error(message || 'Bild konnte nicht aktualisiert werden');
  }

  try {
    const payload = (await response.json()) as ImageUploadResponse | null;
    const result = payload?.results?.find((item) => item.success);
    return result?.record ?? null;
  } catch {
    return null;
  }
};

export const deleteImageFromPool = async (imageId: string) => {
  if (!imageId) {
    throw new Error('Invalid image id.');
  }

  const token = await resolveAuthToken();
  if (!token) {
    throw new Error('User is not authenticated');
  }

  const response = await fetch(
    `${publicBaseUrl}/image-pool/pool/${encodeURIComponent(imageId)}`,
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      mode: 'cors',
    },
  );

  if (!response.ok) {
    const message = await readErrorMessage(response);
    throw new Error(message || 'Bild konnte nicht gelöscht werden');
  }

  return true;
};

export const listImages = async (
  fetcher: typeof fetch = fetch,
): Promise<ImagePoolItem[]> => {
  const headers = await buildOptionalAuthHeaders({
    Accept: 'application/json',
  });
  const response = await fetcher(`${publicBaseUrl}/public/images`, {
    method: 'POST',
    headers,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const payload = (await response.json()) as
    | ImageListResponse
    | ImagePoolItem[];
  const items = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.images)
      ? payload.images
      : Array.isArray(payload?.items)
        ? payload.items
        : [];

  const normalizedItems = items.map((item) => ({
    ...(item as unknown as Record<string, unknown>),
    ...(item as ImagePoolItem),
    createdAt:
      typeof item.createdAt === 'string'
        ? item.createdAt
        : typeof (item as unknown as Record<string, unknown>).created_at ===
            'string'
          ? String((item as unknown as Record<string, unknown>).created_at)
          : typeof (item as unknown as Record<string, unknown>).uploadedAt ===
              'string'
            ? String((item as unknown as Record<string, unknown>).uploadedAt)
            : typeof (item as unknown as Record<string, unknown>).uploaded_at ===
                'string'
              ? String((item as unknown as Record<string, unknown>).uploaded_at)
              : undefined,
    source: item.source ?? item.filename ?? item.id,
  }));

  /**
   * Parses image timestamps for sorting while treating invalid values as missing.
   */
  const toTimestamp = (value?: string) => {
    if (!value) {
      return Number.NaN;
    }

    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }

    return Date.parse(value.replace(' ', 'T'));
  };

  return normalizedItems.sort((left, right) => {
    const rightTimestamp = toTimestamp(right.createdAt);
    const leftTimestamp = toTimestamp(left.createdAt);
    const leftHasTimestamp = Number.isFinite(leftTimestamp);
    const rightHasTimestamp = Number.isFinite(rightTimestamp);

    if (leftHasTimestamp && rightHasTimestamp && leftTimestamp !== rightTimestamp) {
      return rightTimestamp - leftTimestamp;
    }
    if (leftHasTimestamp && !rightHasTimestamp) {
      return -1;
    }
    if (!leftHasTimestamp && rightHasTimestamp) {
      return 1;
    }
    return String(left.id).localeCompare(String(right.id));
  });
};
